// Generic payment processor integration for initiating carbon credit purchases
// This module abstracts initiating a checkout session with a third-party provider
// (Stripe, PayPal, or custom). It expects a backend endpoint that creates the
// provider session and returns a URL to redirect the buyer.

/*
Successful payment (no 3DS):

Card: 4242 4242 4242 4242
Expiry: any future date (e.g., 05/27)
CVC: any 3 digits (e.g., 123)
Name/Email: any values
Requires 3D Secure (use this to test the challenge flow):

Card: 4000 0027 6000 3184
Expiry/CVC: any valid
When the 3DS test page appears, click “Complete” (or “Fail” to simulate a failed auth)
Insufficient funds (to test a decline):

Card: 4000 0000 0000 9995
Expiry/CVC: any valid
Result: payment is declined with an insufficient_funds message

*/




/**
 * Extract a numeric price (and optionally currency) from a retirement metadata object.
 * @param {object} retirement - Selected retirement/certificate object
 * @returns {{ unitAmount: number | null, currency: string }}
 */
export function extractPriceFromRetirement(retirement) {
  const currency = process.env.REACT_APP_PAYMENT_CURRENCY || 'AUD';

  const parseMoney = (raw) => {
    if (raw == null) return null;
    if (typeof raw === 'number') {
      return Number.isFinite(raw) ? raw : null;
    }
    const text = String(raw).trim();
    if (!text) return null;

    // Accept values like "37.8", "A$ 37.8", "AUD 37.8" and keep decimals.
    const normalized = text
      .replace(/,/g, '')
      .replace(/[^0-9.\-]/g, '');
    if (!normalized) return null;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
  };

  try {
    const attrs = retirement?.metadata?.attributes || [];
    const priceAttr = attrs.find(a => `${a?.trait_type}`.toLowerCase() === 'price');

    // Prefer an explicit per-record field when present, then metadata trait.
    const fromRecord = parseMoney(retirement?.unitPrice ?? retirement?.price);
    const fromMetadata = parseMoney(priceAttr?.value);
    const unitAmount = fromRecord ?? fromMetadata;

    return { unitAmount, currency };
  } catch {
    return { unitAmount: null, currency };
  }
}

/**
 * Build a default purchase payload for the backend from a retirement record
 * @param {object} params
 * @param {object} params.retirement - retirement/certificate object
 * @param {number} [params.quantity=1] - number of units (tons) to purchase
 * @param {string} [params.buyerAddress] - optional buyer wallet address
 * @param {string} [params.source='registry-dialog'] - source tag
 */
export function buildPurchasePayload({ retirement, quantity = 1, buyerAddress, source = 'registry-dialog' }) {
  const { unitAmount, currency } = extractPriceFromRetirement(retirement);
  const fallbackPrice = Number(process.env.REACT_APP_DEFAULT_CARBON_PRICE || 38.5);
  const rawPricePerUnit = unitAmount ?? fallbackPrice;
  // Normalize to 2 decimals for currency precision sent to Stripe.
  const pricePerUnit = Number(rawPricePerUnit.toFixed(2));
  const name = retirement?.name || `Carbon Credit`;
  const tokenId = retirement?.tokenId ?? retirement?.id;
  const description = retirement?.description || 'Purchase of renewable energy carbon credit';

  const metadata = {
    tokenId: tokenId?.toString?.() || `${tokenId}`,
    device: retirement?.device,
    retiredBy: retirement?.retiredBy,
    txHash: retirement?.txHash,
    amount: retirement?.amount,
    timestamp: retirement?.timestamp,
    source,
  };

  // Resolve dynamic return URLs. If env URLs are placeholders (contain 'yourapp') or empty,
  // fall back to the current app's registry route and include tokenId on success for UI updates.
  const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const looksPlaceholder = (u) => !u || /yourapp/i.test(String(u));
  let successUrl = process.env.REACT_APP_PAYMENT_SUCCESS_URL;
  let cancelUrl = process.env.REACT_APP_PAYMENT_CANCEL_URL;
  if (looksPlaceholder(successUrl)) {
    successUrl = `${appOrigin}/registry?payment=success${tokenId != null ? `&tokenId=${encodeURIComponent(tokenId)}` : ''}`;
  }
  if (looksPlaceholder(cancelUrl)) {
    cancelUrl = `${appOrigin}/registry?payment=cancel`;
  }

  return {
    provider: process.env.REACT_APP_PAYMENT_PROVIDER || 'custom',
    successUrl,
    cancelUrl,
    lineItems: [
      {
        name,
        description,
        quantity: Number(quantity) || 1,
        unitAmount: pricePerUnit,
        currency,
        metadata,
      },
    ],
    buyer: {
      walletAddress: buyerAddress || null,
    },
  };
}

/**
 * Initiates a checkout session via the backend and returns the redirect URL
 * The backend should create a provider-specific session and return { url }
 * @param {object} payload - purchase payload from buildPurchasePayload
 * @returns {Promise<string>} - redirect URL
 */
export async function initiateCheckout(payload) {
  const apiBaseRaw = process.env.REACT_APP_PAYMENT_API || 'https://serenity.ing/payments';
  let base = (apiBaseRaw || '').toString().trim();
  const isAbsolute = /^https?:\/\//i.test(base);
  // If not absolute, ensure leading slash for same-origin routes
  if (!isAbsolute) {
    if (!base.startsWith('/')) base = `/${base}`;
  }
  // Trim single trailing slash
  base = base.replace(/\/$/, '');
  const endpoint = `${base}/checkout`;
  const controller = new AbortController();
  const rawTimeout = Number(process.env.REACT_APP_PAYMENT_TIMEOUT_MS || 45000);
  const timeoutMs = Number.isFinite(rawTimeout) ? rawTimeout : 45000;
  const id = timeoutMs > 0 ? setTimeout(() => controller.abort('timeout'), timeoutMs) : null;
  try {
    const tryEndpoints = [];
    // If an absolute backend URL is provided, use it exclusively
    if (isAbsolute) {
      tryEndpoints.push(endpoint);
    } else {
      // Relative config: prefer an absolute origin from env, then same-origin
      // 1) From REACT_APP_PAYMENT_ORIGIN if provided
      const envOrigin = process.env.REACT_APP_PAYMENT_ORIGIN;
      if (envOrigin && /^https?:\/\//i.test(envOrigin)) {
        tryEndpoints.push(`${envOrigin.replace(/\/$/, '')}${endpoint}`);
      } else {
        // 2) Derive origin from REACT_APP_API_URL if absolute
        const apiUrl = process.env.REACT_APP_API_URL;
        try {
          if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
            const origin = new URL(apiUrl).origin;
            tryEndpoints.push(`${origin}${endpoint}`);
          }
        } catch (_) { /* noop */ }
      }
      // 3) Configured relative path (same origin)
      tryEndpoints.push(endpoint);
      // 4) Explicit same-origin /payments/checkout
      try {
        const currentOrigin = window?.location?.origin;
        if (currentOrigin) {
          tryEndpoints.push(`${currentOrigin.replace(/\/$/, '')}/payments/checkout`);
        }
      } catch (_) { /* noop */ }
    }

    let lastErr = null;
    const attempted = [];
    for (const urlToTry of tryEndpoints) {
      try {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[payments] POST', urlToTry);
        }
        attempted.push(urlToTry);
        const res = await fetch(urlToTry, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          // 404: try next fallback
          if (res.status === 404) {
            lastErr = new Error(`404 at ${urlToTry}: ${text || res.statusText}`);
            continue;
          }
          throw new Error(`Checkout init failed (${res.status}) at ${urlToTry}: ${text || res.statusText}`);
        }
        const data = await res.json().catch(() => ({}));
        const redirectUrl = data?.url || data?.approveLink || data?.checkoutUrl;
        if (typeof redirectUrl !== 'string' || !/^https?:\/\//i.test(redirectUrl)) {
          throw new Error('Backend did not return a valid checkout URL');
        }
        return redirectUrl;
      } catch (e) {
        // Provide clearer message on fetch AbortError
        if (e?.name === 'AbortError' || e?.message?.toLowerCase?.().includes('aborted')) {
          lastErr = new Error(`Request timed out after ${timeoutMs}ms at ${urlToTry}. This is often caused by CORS preflight not responding or a slow/unreachable server.`);
        } else {
          lastErr = e;
        }
        // Try next candidate
      }
    }
    const msg = `Unable to initiate checkout. Tried: ${attempted.join(', ')}. Last error: ${lastErr?.message || 'unknown'}`;
    throw new Error(msg);
  } finally {
    if (id) clearTimeout(id);
  }
}

async function fetchRealtimeAccuSpotPrice() {
  const retirementBase = process.env.REACT_APP_RETIREMENT_API || 'https://serenity.ing/retirement';
  const endpoint = `${String(retirementBase).replace(/\/$/, '')}/api/accu/spot`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort('timeout'), 12000);

  try {
    const res = await fetch(endpoint, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    });

    if (!res.ok) {
      throw new Error(`Live price request failed (${res.status})`);
    }

    const data = await res.json();
    const value = Number(data?.price);
    if (!data?.success || !Number.isFinite(value) || value <= 0) {
      throw new Error('Live ACCU price is invalid');
    }

    return {
      unitAmount: Number(value.toFixed(2)),
      currency: String(data?.currency || 'AUD').toUpperCase(),
      source: data?.source || endpoint,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * High-level helper to purchase a carbon credit from a retirement record.
 * Returns true if a redirect was attempted/opened.
 */
export async function purchaseCarbonCredit({ retirement, quantity = 1, buyerAddress, openInNewTab = true }) {
  const payload = buildPurchasePayload({ retirement, quantity, buyerAddress });

  // Force live pricing at checkout creation time.
  const live = await fetchRealtimeAccuSpotPrice();
  if (Array.isArray(payload?.lineItems) && payload.lineItems[0]) {
    payload.lineItems[0].unitAmount = live.unitAmount;
    payload.lineItems[0].currency = live.currency;
    payload.lineItems[0].metadata = {
      ...(payload.lineItems[0].metadata || {}),
      pricingMode: 'realtime',
      priceSource: live.source,
    };
  }

  const url = await initiateCheckout(payload);
  if (openInNewTab) {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      // If popup blocked, fallback to same-tab navigation
      window.location.href = url;
      return true;
    }
    return true;
  }
  window.location.href = url;
  return true;
}

export default {
  extractPriceFromRetirement,
  buildPurchasePayload,
  initiateCheckout,
  purchaseCarbonCredit,
};
